/**
 * Chart-specific DuckDB SQL generator.
 *
 * Wraps the standard `rift()` output as a CTE (`_base`) and layers on
 * chart-aware transforms: date sorting, x-axis grouping, pie aggregation,
 * top-N bucketing, null filtering, and row limiting.
 *
 * The result is a standalone SQL string that produces chart-ready rows.
 * Its hash becomes the `chartRiftId`, allowing charts and tables to
 * maintain independent data pipelines from the same underlying dataset.
 */

import type { DataType } from "@repo/lanes";

import {
  type GraphConfig,
  isCartesianChart,
  isPolarChart,
} from "../types/graph-config";
import {
  aggregatePie,
  applyRowLimit,
  bucketPieSlices,
  buildChartOrderBy,
  DEFAULT_PIE_SLICE_LIMIT,
  filterNulls,
  groupCartesianAxis,
} from "./helpers/chart-transforms";
import { escapeIdentifier } from "./helpers/escape-identifier";
import type { RelativeWindowOptions } from "./helpers/filter-to-sql";
import { riftOutputColumnTypes } from "./helpers/project-columns";
import { rift } from "./rift";
import type { ColumnsConfig, FilterType, RiftConfig } from "./types";

/**
 * Generates chart-optimized DuckDB SQL by wrapping the base rift query and
 * applying transforms appropriate for the given chart type and column types.
 *
 * @param tableNameStr  - DuckDB table name (e.g. `{runId}.arrow`)
 * @param riftConfig    - User's rift config (groups, aggregates, filters, sorts, limit)
 * @param boardFilters  - Board-level filters
 * @param columnDefs    - Column name → DataType map from the dataset run
 * @param graphConfig   - The chart configuration (type, keys, etc.)
 * @param columnsConfig - Per-column display config (format.dateFormat drives date truncation in GROUP BY)
 * @param opts          - Relative window options (e.g. browser-local `now`) for date filters
 * @returns SQL string ready for `duckdb.queryTable()`
 */
export const chartRift = (
  tableNameStr: string,
  riftConfig: RiftConfig | null,
  boardFilters: FilterType[] | null,
  columnDefs: Record<string, DataType> | null,
  graphConfig: GraphConfig,
  columnsConfig?: ColumnsConfig | null,
  opts?: RelativeWindowOptions,
): string => {
  // Start with the standard rift SQL as our base data source.
  const baseSql = rift(
    tableNameStr,
    riftConfig,
    boardFilters,
    columnDefs,
    columnsConfig,
    opts,
  );
  const chartType = graphConfig.type;

  // The chart transforms operate on `_base` (the rift output), so they must
  // reason about EFFECTIVE post-rift column types — e.g. a text column
  // aggregated with COUNT is numeric in `_base`. Deriving these from the same
  // shared projection used by the axis pickers keeps SUM-vs-COUNT decisions
  // consistent with what the user can pick.
  const effectiveColumnDefs = riftOutputColumnTypes(riftConfig, columnDefs);

  // -----------------------------------------------------------------------
  // Cartesian charts: line, area, bar, scatter
  // -----------------------------------------------------------------------
  if (isCartesianChart(chartType)) {
    return buildCartesianSql(
      baseSql,
      riftConfig,
      effectiveColumnDefs,
      graphConfig,
      columnsConfig,
    );
  }

  // -----------------------------------------------------------------------
  // Polar charts: pie (rendered as donut)
  // -----------------------------------------------------------------------
  if (isPolarChart(chartType)) {
    return buildPolarSql(baseSql, effectiveColumnDefs, graphConfig);
  }

  // Fallback: return the base SQL unchanged for unknown chart types.
  return baseSql;
};

// ---------------------------------------------------------------------------
// Cartesian chart SQL builder
// ---------------------------------------------------------------------------

const buildCartesianSql = (
  baseSql: string,
  riftConfig: RiftConfig | null,
  columnDefs: Record<string, DataType> | null,
  graphConfig: GraphConfig,
  columnsConfig?: ColumnsConfig | null,
): string => {
  const xKey = graphConfig.xKey;
  const yKeys = graphConfig.yKeys ?? [];
  const chartType = graphConfig.type;

  // Without an xKey we can't apply any meaningful chart transform.
  if (!xKey) return baseSql;

  const xType = columnDefs?.[xKey];
  const hasExistingGroups =
    riftConfig != null && Object.values(riftConfig.groups ?? {}).some(Boolean);
  const existingSorts = riftConfig?.sorts ?? [];
  const existingLimit = riftConfig?.limit;

  // Attempt x-axis grouping (deduplicates categories / aggregates values).
  const groupResult = groupCartesianAxis(
    xKey,
    yKeys,
    columnDefs,
    chartType,
    hasExistingGroups,
    columnsConfig,
  );

  const outputColumns = groupResult
    ? new Set([xKey, ...yKeys])
    : new Set([xKey, ...yKeys]);
  const orderBy = buildChartOrderBy(existingSorts, outputColumns, xKey, xType);

  // Filter nulls on the x-axis so charts don't render blank categories.
  const nullFilter = filterNulls([xKey]);

  // Performance row limit.
  const rowLimit = applyRowLimit(chartType, existingLimit);

  // If no transforms are needed, return the base SQL as-is.
  if (!groupResult && !orderBy && !nullFilter && !rowLimit) {
    return baseSql;
  }

  // Build the final SQL with _base CTE.
  const parts: string[] = [`WITH _base AS (\n  ${baseSql}\n)`];

  if (groupResult) {
    // Grouped query: SELECT agg'd columns FROM _base WHERE ... GROUP BY ...
    parts.push(`SELECT ${groupResult.selectClause}`);
    parts.push("FROM _base");
    if (nullFilter) parts.push(nullFilter);
    parts.push(groupResult.groupByClause);
  } else {
    // Non-grouped: pass through all columns but may still filter / sort / limit.
    const selectCols = [xKey, ...yKeys];
    parts.push(`SELECT ${selectCols.map(escapeIdentifier).join(", ")}`);
    parts.push("FROM _base");
    if (nullFilter) parts.push(nullFilter);
  }

  if (orderBy) parts.push(orderBy);
  if (rowLimit) parts.push(rowLimit);

  return parts.join("\n");
};

// ---------------------------------------------------------------------------
// Polar (pie) chart SQL builder
// ---------------------------------------------------------------------------

const buildPolarSql = (
  baseSql: string,
  columnDefs: Record<string, DataType> | null,
  graphConfig: GraphConfig,
): string => {
  const angleKey = graphConfig.angleKey;
  const sectorLabelKey = graphConfig.sectorLabelKey;

  // Need both keys to build a meaningful pie chart query.
  if (!angleKey || !sectorLabelKey) return baseSql;

  const pieSliceLimit = graphConfig.pieSliceLimit ?? DEFAULT_PIE_SLICE_LIMIT;
  const pieAgg = aggregatePie(angleKey, sectorLabelKey, columnDefs);

  // When pieSliceLimit is 0 the user opted out of "Other" bucketing — skip
  // the ranking/bucketing CTEs and return the aggregation directly.
  if (pieSliceLimit === 0) {
    return [
      `WITH _base AS (\n  ${baseSql}\n),`,
      `_pie_agg AS (\n  ${pieAgg.sql}\n)`,
      "SELECT * FROM _pie_agg",
    ].join("\n");
  }

  const bucket = bucketPieSlices(
    pieSliceLimit,
    pieAgg.angleAlias,
    pieAgg.sectorLabelAlias,
  );

  // Chain of CTEs: _base → _pie_agg → _pie_ranked → final SELECT
  return [
    `WITH _base AS (\n  ${baseSql}\n),`,
    `_pie_agg AS (\n  ${pieAgg.sql}\n),`,
    `_pie_ranked AS (\n  ${bucket.rankedCte}\n)`,
    bucket.finalSelect,
  ].join("\n");
};
